import { API_BASE_URL } from "../config";
import type {
  AuthorizeAppParams,
  AuthorizeResponse,
  ConsentResponse,
  ExchangeTokenParams,
  LoginParams,
  LoginResponse,
  Resend2FACodeParams,
  Resend2FACodeResponse,
  SubmitConsentParams,
  TokenResponse,
} from "../types/auth";

export async function login({
  username,
  password,
  googleIdToken,
  twoFaToken,
}: LoginParams): Promise<LoginResponse> {
  try {
    let body: any;
    if (twoFaToken) {
      body = {
        twoFactorAuthCode: twoFaToken,
        googleIdToken: googleIdToken,
        userName: username,
        password: password,
      };
    } else if (googleIdToken) {
      body = { googleIdToken: googleIdToken };
    } else if (username && password) {
      body = { userName: username, password: password };
    } else {
      throw new Error("Either Google ID token or username/password is required");
    }
    
    const response = await fetch(`${API_BASE_URL}/oauth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    const responseText = await response.text();

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error("Failed to parse login response as JSON:", parseError);
      throw new Error(`Invalid response from server: ${responseText}`);
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Invalid credentials. Please check your username and password.");
      } else if (response.status === 403) {
        throw new Error("Account is disabled or deleted. Please contact support.");
      } else {
        throw new Error(
          data?.message ||
            data?.Error ||
            `Login failed with status ${response.status}: ${responseText}`
        );
      }
    }

    return data;
  } catch (error) {
    console.error("Login Error:", error);
    throw error;
  }
}

export async function resend2FACode({
  userName,
  password,
  googleIdToken,
}: Resend2FACodeParams): Promise<Resend2FACodeResponse> {
  try {
    const requestBody: Record<string, any> = {};
    if (userName && password) {
      requestBody.userName = userName;
      requestBody.password = password;
    }
    if (googleIdToken) {
      requestBody.googleIdToken = googleIdToken;
    }

    const response = await fetch(`${API_BASE_URL}/api/identity/2fa/code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const safeJsonParse = async (response: Response) => {
      const text = await response.text();
      if (!text || text.trim() === "") {
        return {};
      }
      try {
        return JSON.parse(text);
      } catch {
        return { message: text };
      }
    };

    if (!response.ok) {
      const errorData = await safeJsonParse(response);

      if (response.status === 400) {
        throw new Error("Invalid parameters. Please check your input.");
      } else if (response.status === 401) {
        throw new Error("Unauthorized. Please check your credentials.");
      } else {
        throw new Error(
          errorData?.Error ||
            errorData?.message ||
            `Failed to resend 2FA code with status ${response.status}`
        );
      }
    }

    const responseData = await safeJsonParse(response);

    return {
      Success: true,
      Message:
        responseData?.message ||
        responseData?.Message ||
        "Two-factor authentication code resent successfully.",
      ...responseData,
    };
  } catch (error) {
    console.error("Resend 2FA Code Error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("An unexpected error occurred while resending 2FA code");
  }
}

export function buildAuthorizeUrl({
  clientId,
  redirectUri,
  scopes,
  state,
  responseType,
  codeChallenge,
  codeChallengeMethod,
  resource,
  prompt,
}: Omit<AuthorizeAppParams, "bearerToken">): string {
  const queryParams = new URLSearchParams({
    response_type: responseType || "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    state: state,
    scope: scopes,
  });

  if (codeChallenge) queryParams.set("code_challenge", codeChallenge);
  if (codeChallengeMethod) queryParams.set("code_challenge_method", codeChallengeMethod);
  if (resource) queryParams.set("resource", resource);
  if (prompt) queryParams.set("prompt", prompt);

  return `${API_BASE_URL}/oauth/authorize?${queryParams.toString()}`;
}

export async function authorizeApp({
  bearerToken,
  ...authorizeParams
}: AuthorizeAppParams): Promise<AuthorizeResponse> {
  try {
    const response = await fetch(buildAuthorizeUrl(authorizeParams), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 400) {
        throw new Error("Invalid parameters or client ID. Please check your configuration.");
      } else if (response.status === 401) {
        throw new Error("Invalid or expired authentication token. Please login again.");
      } else if (response.status === 403) {
        throw new Error(
          "Insufficient access level for authorization. This usually means your account doesn't have the required permissions or the application you're trying to authorize is not available for your account type."
        );
      } else {
        throw new Error(
          errorData?.Error ||
            errorData?.message ||
            `Authorization failed with status ${response.status}`
        );
      }
    }

    const data = await response.json();

    if (data?.consent_required) {
      return {
        ConsentRequired: true,
        SessionKey: data?.session_key,
      };
    }

    return {
      ConsentRequired: false,
      Code: data?.code,
      grantedScopes: data?.granted_scopes,
    };
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error(
        "Network error: Unable to connect to the authorization server. Please check your internet connection and try again."
      );
    } else if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("An unexpected error occurred during authorization. Please try again.");
    }
  }
}

export async function submitConsent({
  bearerToken,
  sessionKey,
  consentGiven,
}: SubmitConsentParams): Promise<ConsentResponse> {
  const response = await fetch(`${API_BASE_URL}/oauth/consent`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      session_key: sessionKey,
      consent_given: consentGiven,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 401) {
      throw new Error("Authentication token expired. Please login again.");
    } else if (response.status === 403) {
      throw new Error(
        "Access denied. You may not have permission to give consent for this application."
      );
    } else if (response.status === 400) {
      throw new Error("Invalid consent request. Please try again.");
    } else {
      throw new Error(
        errorData?.Error ||
          errorData?.message ||
          `Consent submission failed with status ${response.status}`
      );
    }
  }
  return response.json();
}

export async function exchangeToken({
  code,
  clientId,
  clientSecret,
  redirectUri,
}: ExchangeTokenParams): Promise<TokenResponse> {
  try {
    const requestBody = {
      grant_type: "authorization_code",
      code: code,
      client_id: clientId,
      client_secret: clientSecret,
      redirect_uri: redirectUri,
    };

    const response = await fetch(`${API_BASE_URL}/oauth/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 400) {
        throw new Error("Invalid authorization code or client credentials.");
      } else if (response.status === 401) {
        throw new Error("Invalid client secret or expired authorization code.");
      } else {
        throw new Error(
          errorData?.Error ||
            errorData?.message ||
            `Token exchange failed with status ${response.status}`
        );
      }
    }

    const data = await response.json();

    return {
      AccessToken: data.access_token,
    };
  } catch (error) {
    console.error("Token Exchange Error:", error);
    throw error;
  }
}
